package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.GameTable
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.domain.usecase.GetCharacterByIdUseCase
import fr.manyverse.app.domain.usecase.GetTableByIdUseCase
import fr.manyverse.app.domain.usecase.ObserveGameTableMembersUseCase
import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.error_table_not_found
import org.jetbrains.compose.resources.getString

data class ActiveGameData(
    val table: GameTable,
    val characters: List<Character>,
    val tableMembers: List<TableMember>,
    val isGameMaster: Boolean
)

class ActiveGameViewModel(
    private val getTableByIdUseCase: GetTableByIdUseCase,
    private val observeGameTableMembersUseCase: ObserveGameTableMembersUseCase,
    private val getCharacterByIdUseCase: GetCharacterByIdUseCase
) : BaseViewModel() {

    private val _activeGameState =
        MutableStateFlow<AsyncUiState<ActiveGameData>>(AsyncUiState.Loading)
    val activeGameState: StateFlow<AsyncUiState<ActiveGameData>> = _activeGameState.asStateFlow()

    private val _actionState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val actionState: StateFlow<AsyncUiState<Unit>> = _actionState.asStateFlow()

    private var observeJob: Job? = null
    private var currentTable: GameTable? = null

    fun loadAndObserveActiveGame(tableId: String, currentUserId: String) {
        viewModelScope.launch {
            _activeGameState.value = AsyncUiState.Loading

            getTableByIdUseCase(tableId)
                .onSuccess { table ->
                    if (table != null) {
                        observeActiveGame(table, currentUserId)
                    } else {
                        _activeGameState.value = AsyncUiState.Error(getString(Res.string.error_table_not_found))
                    }
                }
                .onFailure { exception ->
                    _activeGameState.value = AsyncUiState.Error(exception.toUserMessage())
                }
        }
    }

    private fun observeActiveGame(
        table: GameTable,
        currentUserId: String
    ) {
        currentTable = table
        val isGameMaster = table.gameMasterId == currentUserId

        observeJob?.cancel()

        observeJob = viewModelScope.launch {
            observeGameTableMembersUseCase(table.id)
                .catch { exception ->
                    _activeGameState.value = AsyncUiState.Error(exception.toUserMessage())
                }
                .collect { members ->
                    val characters = members
                        .filter { it.characterId != null && it.userId != table.gameMasterId }
                        .mapNotNull { member ->
                            getCharacterByIdUseCase(member.characterId!!).getOrElse { error ->
                                // Un personnage manquant ferait disparaître son joueur de l'écran :
                                // on garde l'état affiché, ou on montre l'erreur au premier chargement
                                if (_activeGameState.value !is AsyncUiState.Success) {
                                    _activeGameState.value = AsyncUiState.Error(error.toUserMessage())
                                }
                                return@collect
                            }
                        }

                    _activeGameState.value = AsyncUiState.Success(
                        ActiveGameData(
                            table = table,
                            characters = characters,
                            tableMembers = members,
                            isGameMaster = isGameMaster
                        )
                    )
                }
        }
    }

    fun stopObservingActiveGame() {
        observeJob?.cancel()
        observeJob = null
    }

    fun resetActionState() {
        resetState(_actionState)
    }

    override fun onCleared() {
        super.onCleared()
        stopObservingActiveGame()
    }
}
